import { tick } from "svelte";
import { projectState } from "@app/store/projectStore.svelte.js";
import {
    eventGroupState,
    resetEventGroupState,
} from "@app/store/eventGroupStore.svelte.js";
import { setSpeakerState } from "@app/store/speakerStore.svelte.js";
import { setMetadataState } from "@app/store/metadataStore.svelte.js";
import { setIssues } from "@app/store/issueStore.svelte.js";
import { resetMarkerStateWithData } from "@app/store/markerStore.svelte.js";
import { setStyleState } from "@app/store/styleStore.svelte.js";
import _EventGroup from "@cclib/classes/eventGroup.js";
import _Event from "@cclib/classes/event.js";

const SYNC_STATES = new Set(["created", "updated", "deleted"]);

function toLiveEvent(eventItem) {
    const teamEv = { ...new _Event(eventItem) };
    teamEv.id = eventItem.id;
    return teamEv;
}

function toLiveGroup(teamEventGroup) {
    const events = [];
    for (const eventItem of teamEventGroup.events || []) {
        if (eventItem.state === "deleted") {
            continue;
        }
        events.push(toLiveEvent(eventItem));
    }

    const groupOpts = {
        ...teamEventGroup,
        events: [],
        selected: [],
    };
    if (SYNC_STATES.has(groupOpts.state)) {
        delete groupOpts.state;
    }

    const teamEvGroup = { ...new _EventGroup(groupOpts) };
    teamEvGroup.id = teamEventGroup.id;
    teamEvGroup.events = events;
    teamEvGroup.scrollPosition = 0;
    return teamEvGroup;
}

function applyProjectStores(commitFileJson) {
    if (Object.prototype.hasOwnProperty.call(commitFileJson, "speakers") && Array.isArray(commitFileJson.speakers)) {
        setSpeakerState(commitFileJson.speakers);
    }
    if (Object.prototype.hasOwnProperty.call(commitFileJson, "style") && commitFileJson.style) {
        setStyleState(commitFileJson.style);
    }
    if (Object.prototype.hasOwnProperty.call(commitFileJson, "markers") && commitFileJson.markers) {
        resetMarkerStateWithData(commitFileJson.markers);
    }
    if (Object.prototype.hasOwnProperty.call(commitFileJson, "issues") && Array.isArray(commitFileJson.issues)) {
        setIssues(commitFileJson.issues);
    }
    if (Object.prototype.hasOwnProperty.call(commitFileJson, "metadata") && Array.isArray(commitFileJson.metadata)) {
        setMetadataState(commitFileJson.metadata);
    }
}

/**
 * Apply a team commit JSON to live stores.
 * @param {object} commitFileJson
 * @param {{ replaceAll?: boolean, applyIdentity?: boolean }} [options]
 * @returns {Promise<{ eventGroupsNotInShare: string[] }>}
 */
export async function applyTeamCommit(commitFileJson, options = {}) {
    const { replaceAll = false, applyIdentity = false } = options;
    const commitGroups = commitFileJson.eventGroups || [];

    let selectedEventGroupId = false;
    if (projectState.selected > -1 && eventGroupState[projectState.selected]) {
        selectedEventGroupId = eventGroupState[projectState.selected].id;
    }

    const eventGroupsNotInShare = [];
    if (!replaceAll) {
        for (const eventGroup of eventGroupState) {
            const inCommit = commitGroups.some((shareEventGroup) => shareEventGroup.id === eventGroup.id);
            if (!inCommit) {
                eventGroupsNotInShare.push(eventGroup.name);
            }
        }
    }

    projectState.selected = false;
    await tick();

    projectState.name = commitFileJson.name || projectState.name || "Untitled";
    projectState.commit = commitFileJson.id;
    if (commitFileJson.frameRate != null && commitFileJson.frameRate !== "") {
        projectState.frameRate = parseFloat(commitFileJson.frameRate);
    }
    if (commitFileJson.dropFrame != null && commitFileJson.dropFrame !== "") {
        projectState.dropFrame = commitFileJson.dropFrame === true || commitFileJson.dropFrame === "true";
    }

    if (applyIdentity) {
        if (commitFileJson.ownerId) {
            projectState.ownerId = commitFileJson.ownerId;
        }
        projectState.type = "team";
        if (commitFileJson.teamId) {
            projectState.teamId = commitFileJson.teamId;
        }
        if (commitFileJson.media) {
            projectState.media = commitFileJson.media;
        }
    }

    if (replaceAll) {
        resetEventGroupState();
        await tick();
        for (const teamEventGroup of commitGroups) {
            if (teamEventGroup.state === "deleted") {
                continue;
            }
            eventGroupState.push(toLiveGroup(teamEventGroup));
        }
    } else {
        const deletedIds = new Set(
            commitGroups.filter((group) => group.state === "deleted").map((group) => group.id),
        );
        for (let i = eventGroupState.length - 1; i >= 0; i--) {
            if (deletedIds.has(eventGroupState[i].id)) {
                eventGroupState.splice(i, 1);
            }
        }

        for (const teamEventGroup of commitGroups) {
            if (teamEventGroup.state === "deleted") {
                continue;
            }
            const liveGroup = toLiveGroup(teamEventGroup);
            const existingIndex = eventGroupState.findIndex((eventGroup) => eventGroup.id === liveGroup.id);
            if (existingIndex === -1) {
                eventGroupState.push(liveGroup);
            } else {
                eventGroupState[existingIndex] = liveGroup;
            }
        }
    }

    applyProjectStores(commitFileJson);
    await tick();

    if (selectedEventGroupId !== false) {
        const indexOfSelectedEventGroup = eventGroupState.findIndex((eventGroup) => eventGroup.id === selectedEventGroupId);
        projectState.selected = indexOfSelectedEventGroup > -1 ? indexOfSelectedEventGroup : (eventGroupState.length > 0 ? 0 : false);
    } else if (eventGroupState.length > 0) {
        projectState.selected = 0;
    }

    return { eventGroupsNotInShare };
}
